import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { ArrowRight, Users, Target, Zap, ChevronDown, Play } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const [eventCode, setEventCode] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!eventCode.trim()) {
      toast.error("Please enter an event code");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/events/code/${eventCode.toUpperCase()}`);
      if (response.data) {
        navigate(`/questionnaire/${eventCode.toUpperCase()}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Event not found");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-hidden">
      {/* Hero Section */}
      <div className="relative min-h-screen flex flex-col">
        {/* Background Image - Team Celebrating */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('/team-bg.jpg')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/75 to-white" />
        </div>

        {/* Header */}
        <header className="relative z-10 flex justify-between items-center px-6 md:px-12 py-6">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-lg bg-pink-600 flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading text-xl tracking-wide text-gray-900">TEAMPOWER</span>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <LanguageSwitcher />
            <Button 
              variant="outline" 
              onClick={() => navigate('/admin')}
              className="text-gray-600 hover:text-gray-900 border-gray-300 hover:border-gray-400"
              data-testid="admin-link"
            >
              {t('landing.admin')}
            </Button>
          </motion.div>
        </header>

        {/* Main Content */}
        <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="max-w-3xl"
          >
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-wide uppercase mb-6 text-gray-900">
              {t('landing.title')} <span className="text-pink-600">{t('landing.titleAccent')}</span>
            </h1>
            <p className="text-gray-600 text-base md:text-lg mb-10 max-w-xl mx-auto">
              {t('landing.subtitle')}
            </p>

            {/* Event Code Form */}
            <form onSubmit={handleSubmit} className="max-w-md mx-auto" data-testid="event-code-form">
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="text"
                  placeholder={t('landing.enterCode')}
                  value={eventCode}
                  onChange={(e) => setEventCode(e.target.value.toUpperCase())}
                  className="h-14 bg-white/90 border-gray-300 text-gray-900 text-center text-lg tracking-widest uppercase placeholder:normal-case placeholder:tracking-normal shadow-sm"
                  maxLength={6}
                  data-testid="event-code-input"
                />
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="h-14 px-8 bg-pink-600 hover:bg-pink-700 text-white font-bold shadow-lg hover:shadow-xl transition-all"
                  data-testid="start-button"
                >
                  {loading ? "..." : t('landing.start')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </form>

            {/* Divider */}
            <div className="flex items-center gap-4 max-w-md mx-auto mt-6 mb-2">
              <div className="flex-1 h-px bg-gray-300/60" />
              <span className="text-gray-400 text-sm">{language === 'el' ? 'ή' : 'or'}</span>
              <div className="flex-1 h-px bg-gray-300/60" />
            </div>

            {/* Quick Assessment Button */}
            <Button
              onClick={() => navigate("/assessment")}
              variant="outline"
              className="h-12 px-8 border-pink-300 text-pink-600 hover:bg-pink-50 hover:text-pink-700 font-semibold"
              data-testid="quick-assessment-button"
            >
              <Play className="w-4 h-4 mr-2" />
              {language === 'el' ? 'Γρήγορη Αξιολόγηση' : 'Quick Assessment'}
            </Button>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="absolute bottom-10"
          >
            <ChevronDown className="w-8 h-8 text-gray-400 animate-bounce" />
          </motion.div>
        </main>
      </div>

      {/* Features Section */}
      <section className="py-20 px-6 md:px-12 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-3xl md:text-4xl text-center mb-16 uppercase tracking-wide text-gray-900"
          >
            {t('dimensions.threeDimensions')} <span className="text-pink-600">{t('dimensions.teamPower')}</span>
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Capability */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-blue-500/10 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative bg-white border border-gray-200 rounded-2xl p-8 hover:border-blue-400 hover:shadow-lg transition-all">
                <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="font-heading text-2xl mb-3 text-blue-600">{t('dimensions.capabilityUpper').toUpperCase()}</h3>
                <p className="text-gray-600">
                  {t('dimensions.capabilityDesc')}
                </p>
              </div>
            </motion.div>

            {/* Collaboration */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-purple-500/10 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative bg-white border border-gray-200 rounded-2xl p-8 hover:border-purple-400 hover:shadow-lg transition-all">
                <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center mb-6">
                  <Users className="w-7 h-7 text-purple-600" />
                </div>
                <h3 className="font-heading text-2xl mb-3 text-purple-600">{t('dimensions.collaborationUpper').toUpperCase()}</h3>
                <p className="text-gray-600">
                  {t('dimensions.collaborationDesc')}
                </p>
              </div>
            </motion.div>

            {/* Energy */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-pink-500/10 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative bg-white border border-gray-200 rounded-2xl p-8 hover:border-pink-400 hover:shadow-lg transition-all">
                <div className="w-14 h-14 rounded-xl bg-pink-100 flex items-center justify-center mb-6">
                  <Zap className="w-7 h-7 text-pink-600" />
                </div>
                <h3 className="font-heading text-2xl mb-3 text-pink-600">{t('dimensions.energyUpper').toUpperCase()}</h3>
                <p className="text-gray-600">
                  {t('dimensions.energyDesc')}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-gray-200 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <img 
            src="/summit-logo.png" 
            alt="Summit Logo" 
            className="h-8 object-contain"
          />
          <p className="text-gray-500 text-sm">
            {t('landing.poweredBy')}
          </p>
        </div>
      </footer>
    </div>
  );
}
